/* eslint-disable react/destructuring-assignment */
import React, { useState, useEffect, ChangeEvent } from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';

import {
  BacnetSettings,
  BacnetSettingsUpdate,
  BacnetSettingsCreate,
  SensorLatest,
} from '../../../services/api';
import {
  updateSensorBacnetConfig,
  createSensorBacnetConfig,
  getSensorBacnetConfig,
} from '../../../services/apiService';
import Loading from '../../../components/Loading';
import RemoveBacnetDialog from './RemoveBacnetDialog';
import useStyles from '../../../styles';
import BacnetConfigForm from './BacnetConfigForm';
import { BacnetState } from '../../../Views/SensorConfig';

interface SensorBacnetConfigProps {
  sensorDetails: SensorLatest;
  bacnetState: BacnetState | undefined;
}

function SensorBacnetConfig({ sensorDetails, bacnetState }: SensorBacnetConfigProps): JSX.Element {
  const { classes } = useStyles();
  const [isEditingBacnet, setIsEditingBacnet] = useState(false);
  const [bacnetItem, setBacnetItem] = useState<BacnetSettingsUpdate | BacnetSettingsCreate>();
  const [pageLoading, setPageLoading] = useState<boolean>();
  const [bacnetSetting, setBacnetSetting] = useState<BacnetSettings>();
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [isSettingEditable, setIsSettingEditable] = useState<boolean>();
  const [alertMsg, setAlertMsg] = useState<string>();

  const sensorId = sensorDetails.id;

  useEffect(() => {
    if (bacnetState?.alertMsg) setAlertMsg(bacnetState.alertMsg);
    if (bacnetState?.config) setBacnetSetting(bacnetState.config);
    setIsSettingEditable(bacnetState?.isEditable);
  }, [bacnetState]);

  const getBacnetConfig = () => {
    setPageLoading(true);
    setBacnetSetting(undefined);
    setIsSettingEditable(false);
    if (sensorId) {
      getSensorBacnetConfig(sensorId)
        .then((details) => {
          setBacnetSetting(details);
          setPageLoading(false);
          setIsSettingEditable(true);
        })
        .catch((e) => {
          setIsSettingEditable(false);
          const resStatus = e?.cause?.status ?? undefined;
          if (resStatus && resStatus === 402) setIsSettingEditable(false);
          else if (resStatus && resStatus === 404) setIsSettingEditable(true);
          setAlertMsg(e?.cause?.msg ?? 'Bacnet config not available');
          setPageLoading(false);
        });
    }
  };

  useEffect(() => {
    if (!bacnetState?.config) getBacnetConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bacnetState]);

  const handleConfigChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    key: string,
    configType: string
  ) => {
    const configSettings = { ...bacnetItem };
    const { value } = e.target;
    if (configType === 'sensor') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { sensor } = configSettings;
      if (key === 'wifi_max') {
        if (sensor?.wifi_max) {
          sensor.wifi_max = value;
        }
        if (sensor) {
          Object.assign(sensor, { wifi_max: value });
        } else Object.assign(configSettings, { sensor: { wifi_max: value } });
      }

      if (key === 'bacnet_id') {
        if (sensor?.bacnet_id) {
          sensor.bacnet_id = value;
        }
        if (sensor) {
          Object.assign(sensor, { bacnet_id: value });
        } else Object.assign(configSettings, { sensor: { bacnet_id: value } });
      }
    } else if (configType === 'gateway') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { gateway } = configSettings;
      if (key === 'building_network_no') {
        if (gateway?.building_network_no) {
          gateway.building_network_no = value;
        }
        if (gateway) {
          Object.assign(gateway, { building_network_no: value });
        } else Object.assign(configSettings, { gateway: { building_network_no: value } });
      }
      if (key === 'vlan_network_no') {
        if (gateway?.vlan_network_no) {
          gateway.vlan_network_no = value;
        }
        if (gateway) {
          Object.assign(gateway, { vlan_network_no: value });
        } else Object.assign(configSettings, { gateway: { vlan_network_no: value } });
      }
    }
    setBacnetItem(configSettings as BacnetSettings);
  };

  const createBacnetConfig = (settings: BacnetSettingsCreate) => {
    if (sensorId) {
      createSensorBacnetConfig(sensorId, settings)
        .then(() => {
          getBacnetConfig();
          setBacnetItem(undefined);
          setIsEditingBacnet(false);
          enqueueSnackbar('Successfully created', { variant: 'success' });
        })
        .catch((err) => enqueueSnackbar(err.cause, { variant: 'error', persist: true }));
    }
  };

  const updateBacnetConfig = (settings: BacnetSettingsUpdate) => {
    if (sensorId) {
      updateSensorBacnetConfig(sensorId, settings)
        .then(() => {
          getBacnetConfig();
          setBacnetItem(undefined);
          setIsEditingBacnet(false);
          enqueueSnackbar('Successfully updated', { variant: 'success' });
        })
        .catch((err) => enqueueSnackbar(err.cause, { variant: 'error', persist: true }));
    }
  };

  const requestConfigChange = () => {
    if ((!bacnetSetting || (bacnetSetting && !bacnetSetting?.sensor)) && bacnetItem)
      createBacnetConfig(bacnetItem as BacnetSettingsCreate);
    else if (bacnetItem) updateBacnetConfig(bacnetItem);
  };

  const handleEdit = () => {
    closeSnackbar();
    setAlertMsg(undefined);
    if (isEditingBacnet) {
      setBacnetItem(undefined);
      setIsEditingBacnet(false);
    } else setIsEditingBacnet(true);
  };

  const sensorBacnetInfo = bacnetSetting?.gateway?.sensors.find((config) => config.id === sensorId);

  return (
    <ListItem>
      {showRemoveDialog && sensorId && (
        <RemoveBacnetDialog
          showDialog={showRemoveDialog}
          sensorDetails={sensorDetails}
          hideDialog={() => setShowRemoveDialog(false)}
          refreshConfig={getBacnetConfig}
          setSuccessAlert={() =>
            enqueueSnackbar('Bacnet config has been removed', { variant: 'success' })
          }
        />
      )}
      <Accordion style={{ width: '100%' }}>
        <AccordionSummary
          component="div"
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Badge
            badgeContent={sensorBacnetInfo?.bacnet_id && 'b'}
            invisible={!sensorBacnetInfo}
            color="primary"
            classes={{ badge: classes.customBadge }}
          >
            <DeviceHubIcon className={classes.icon} />
          </Badge>
          <Typography style={{ alignSelf: 'center', marginLeft: '10px' }}>Bacnet Config</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List style={{ width: '100%' }}>
            {alertMsg && (
              <Alert severity="error" className={classes.alertMsg} style={{ marginBottom: '20px' }}>
                {alertMsg}
              </Alert>
            )}

            {pageLoading && <Loading />}
            {isSettingEditable && (
              <BacnetConfigForm
                sensorId={sensorId}
                bacnetConfig={bacnetSetting}
                sensorType={sensorDetails?.type}
                isEditingBacnet={isEditingBacnet}
                bacnetItem={bacnetItem}
                showRemoveDialog={() => setShowRemoveDialog(true)}
                handleEdit={handleEdit}
                handleConfigChange={handleConfigChange}
                requestConfigChange={requestConfigChange}
              />
            )}
          </List>
        </AccordionDetails>
      </Accordion>
    </ListItem>
  );
}

export default SensorBacnetConfig;
